// The support service is its own product on its own host (a Replit app, not Zendesk).
// The Chat tab speaks to the same widget API its floating bubble used to, WITHOUT loading
// that bubble's 546 kB React bundle — `embed.js` renders its own fixed panel into a shadow
// root it builds itself and offers no way to mount it in ours, so its UI could never be the
// Help Hub's. The data is the part we need, and the data is plain HTTP.
export const SUPPORT_API_HOST = import.meta.env.VITE_SUPPORT_API_HOST

// The session JWT the API mints, kept per browser so a reload rejoins the same conversations.
export const SUPPORT_SESSION_KEY = 'cdpSupportSession'

// A conversation's lifecycle, as the support API spells it.
export const SUPPORT_STATUS = Object.freeze({
  OPEN: 'open',
  PENDING: 'pending',
  RESOLVED: 'resolved',
  CLOSED: 'closed',
})

// Still being worked, so it belongs under "Support tickets"; everything else is a previous
// conversation. ONE endpoint feeds both sections — the API has no separate ticket concept.
export const SUPPORT_OPEN_STATUSES = [SUPPORT_STATUS.OPEN, SUPPORT_STATUS.PENDING]

// Whose message a bubble is, as the API spells `senderType`. Anything that isn't the dealer
// is drawn as a reply, so an unknown sender degrades to the assistant's side rather than
// impersonating the dealer.
export const SUPPORT_SENDER = Object.freeze({
  CUSTOMER: 'customer',
  AGENT: 'agent',
  AI: 'ai',
})

// Figma draws this name over every reply.
export const SUPPORT_BOT_NAME = 'Card Dealer Pro Support AI'

// Rows per section before "View all" — three in the design, for all three sections.
export const SUPPORT_SECTION_LIMIT = 3

// Frames the support service sends down its WebSocket. We only act on the first three;
// `ai_progress` and `typing` are listed so an unhandled frame is a deliberate omission
// rather than a silent drop.
export const SUPPORT_WS_EVENT = Object.freeze({
  AUTH_OK: 'auth_ok',
  NEW_MESSAGE: 'new_message',
  CONVERSATION_UPDATED: 'conversation_updated',
  STATUS_CHANGE: 'status_change',
  AI_PROGRESS: 'ai_progress',
  TYPING: 'typing',
})

// Reconnect backoff for that socket: double on each failure, never wait longer than this.
export const SUPPORT_WS_RETRY_MS = 1000
export const SUPPORT_WS_RETRY_MAX_MS = 30000

/**
 * `action` on `POST /widget/conversations/:id/resolution` — the three answers to "Did this
 * resolve your issue?". Read off the support widget's own bundle; the server accepts these
 * literal strings, so they are a contract, not labels.
 */
export const SUPPORT_RESOLUTION = Object.freeze({
  CONFIRM_RESOLVED: 'confirm_resolved',
  NOT_RESOLVED: 'not_resolved',
  REQUEST_HUMAN: 'request_human',
})

/**
 * Attachments. Every value here is the support service's own limit, read off its widget
 * bundle — a file it would reject is better refused in the panel, with a reason, than sent
 * and bounced.
 */
export const SUPPORT_ATTACHMENT_TYPES = Object.freeze([
  'image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'text/plain',
])

// Some browsers hand over an empty `file.type`; the extension is the fallback.
export const SUPPORT_ATTACHMENT_EXTENSIONS = Object.freeze({
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png',
  gif: 'image/gif', webp: 'image/webp', pdf: 'application/pdf', txt: 'text/plain',
})

export const SUPPORT_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024
export const SUPPORT_ATTACHMENT_MAX_FILES = 6

// The dealer's own words open a conversation, so the subject is that line, trimmed to
// something an agent can scan in an inbox.
export const SUPPORT_SUBJECT_MAX = 80
